import { useEffect, useState, useCallback, useRef } from "react";
// import { useProfile } from "../../auth/hooks/useProfile";
import useOrganization from "../../organization/hooks/useOrganization";

import {
  updateTeamMembers,
  getUserOptions,
  getTeamSurvey,
} from "../api/team.api";

import { getTeamsByDepartment } from "../../../components/common/DeptTeamSelector/api/organization.api";

import UserAvatar from "../../../components/ui/icons/UserAvatar";
import { useNavigate } from "react-router-dom";
import { useTeam } from "../contexts/TeamContext";
import { toast } from "react-toastify";

interface TeamState {
  id: number;
  name: string;
  lead_id: number;
  department_id: number;
  members: any[];
  lead?: any;
  completion?: number;
}

const getProgressColor = (pct: number) => {
  if (pct >= 100) return "#10b981";
  if (pct >= 60) return "#0067ff";
  if (pct >= 30) return "#f59e0b";
  return "#ef4444";
};

const UserManagementAccordion = () => {
  // const { user } = useProfile();
  const { currentOrganization } = useOrganization();
  const { selectedDepartment, selectedTeam } = useTeam();
  const navigate = useNavigate();

  const [teams, setTeams] = useState<TeamState[]>([]);
  const [openTeam, setOpenTeam] = useState<number | null>(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [activeIndex, setActiveIndex] = useState<number>(-1);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);

  const [users, setUsers] = useState<any[]>([]);
  const [selectedUser, setSelectedUser] = useState<number | null>(null);
  const [addingTeam, setAddingTeam] = useState<number | null>(null);
  const [removeModal, setRemoveModal] = useState<{
    team: TeamState | null;
    member: any | null;
  }>({ team: null, member: null });
  const dropdownRef = useRef<HTMLDivElement>(null);
  const listRef = useRef<HTMLDivElement>(null);

  /* ================= LOAD TEAMS ================= */

  const loadTeams = useCallback(async () => {
    if (!selectedDepartment && !selectedTeam) return;

    try {
      let teamList: any[] = [];

      // 🔥 PRIORITY 1: If team already selected → use it directly
      if (selectedTeam) {
        teamList = [
          {
            value: selectedTeam.id,
            label: selectedTeam.name,
          },
        ];
      }
      // 🔥 PRIORITY 2: fallback to department
      else if (selectedDepartment) {
        const teamRes = await getTeamsByDepartment(selectedDepartment.id);

        if (!teamRes.success) return;

        teamList = teamRes.data.rows;
      }

      const questionnaireId = currentOrganization?.default_survey
        ? Number(currentOrganization.default_survey)
        : null;

      const detailedTeams = await Promise.all(
        teamList.map(async (t: any) => {
          if (!questionnaireId) return null;

          const survey = await getTeamSurvey({
            team_id: Number(t.value),
            questionnaire_id: questionnaireId,
          });

          if (!survey.success) return null;

          const data = survey.data;

          return {
            id: data.id,
            name: data.name,
            lead_id: data.team_lead?.id,
            department_id: data.department?.id,
            members: data.members ?? [],
            lead: data.team_lead,
            completion: data.team_compl_percentage,
          };
        })
      );

      const cleaned = detailedTeams.filter(Boolean) as TeamState[];

      setTeams(cleaned);
      setOpenTeam(selectedTeam?.id ?? cleaned[0]?.id ?? null);
    } catch (err) {
      console.error("Failed to load teams", err);
    }
  }, [selectedDepartment, selectedTeam, currentOrganization?.default_survey]);

  /* ================= INITIAL LOAD ================= */

  useEffect(() => {
    loadTeams();
  }, [loadTeams]);

  /* ================= LOAD USERS ================= */

  useEffect(() => {
    const fetchUsers = async () => {
      if (!currentOrganization?.id) return;

      const res = await getUserOptions({
        organizationId: currentOrganization.id,
        limit: 100,
        page: 1,
      });

      if (res.success) {
        setUsers(res.data.rows);
      }
    };

    fetchUsers();
  }, [currentOrganization]);

  /* ================= ATTACH USER ================= */

  const handleAttachUser = async (team: TeamState) => {
    if (!selectedUser) {
      alert("Please select a user");
      return;
    }

    const existingIds = team.members.map((m) => m.id);

    if (existingIds.includes(selectedUser)) {
      alert("User already in team");
      return;
    }

    try {
      await updateTeamMembers({
        team_id: team.id,
        name: team.name,
        lead_id: team.lead_id,
        department_id: team.department_id,
        user_ids: [...existingIds, selectedUser],
      });

      toast.success("User Added successfully");
      await loadTeams();

      setSelectedUser(null);
      setAddingTeam(null);
    } catch (err) {
      console.error("Attach user failed", err);
    }
  };

  const confirmRemoveUser = async () => {
    if (!removeModal.team || !removeModal.member) return;

    const team = removeModal.team;
    const memberId = removeModal.member.id;

    try {
      const updatedIds = team.members
        .filter((m) => m.id !== memberId)
        .map((m) => m.id);

      await updateTeamMembers({
        team_id: team.id,
        name: team.name,
        lead_id: team.lead_id,
        department_id: team.department_id,
        user_ids: updatedIds,
      });

      // 🔥 instant UI update (better UX)
      setTeams((prev) =>
        prev.map((t) =>
          t.id === team.id
            ? {
              ...t,
              members: t.members.filter((m) => m.id !== memberId),
            }
            : t
        )
      );
      toast.success("User removed successfully");
      setRemoveModal({ team: null, member: null });
      await loadTeams();
    } catch (err) {
      console.error("Remove user failed", err);
    }
  };

  const filteredUsers = users.filter((u) =>
    u.label.toLowerCase().includes(searchTerm.toLowerCase())
  );

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        setIsDropdownOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);
  useEffect(() => {
  if (!isDropdownOpen) return;
  if (activeIndex < 0) return;

  const container = listRef.current;
  if (!container) return;

  const item = container.children[activeIndex] as HTMLElement;
  if (!item) return;

  const itemTop = item.offsetTop;
  const itemBottom = itemTop + item.offsetHeight;

  const viewTop = container.scrollTop;
  const viewBottom = viewTop + container.clientHeight;

  // scroll up
  if (itemTop < viewTop) {
    container.scrollTop = itemTop;
  }

  // scroll down
  else if (itemBottom > viewBottom) {
    container.scrollTop = itemBottom - container.clientHeight;
  }
}, [activeIndex, isDropdownOpen]);
  /* ================= UI ================= */

  return (
    <div className="space-y-4">
      {teams.map((team) => (
        <div
          key={team.id}
          className="border rounded-2xl bg-white shadow-md w-full"
        >
          {/* HEADER */}
          <div
            onClick={() =>
              setOpenTeam(openTeam === team.id ? null : team.id)
            }
            className="px-4 py-4 sm:px-5 cursor-pointer hover:bg-gray-50"
          >
            <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
              <div className="flex items-center gap-3">
                <i
                  className={`ri-arrow-right-s-line transition ${openTeam === team.id ? "rotate-90" : ""
                    }`}
                />

                <div>
                  <div className="font-medium text-gray-800">
                    {team.name}
                  </div>
                  <div className="text-xs text-gray-500 mt-1">
                    {team.completion}% completed
                  </div>
                </div>

                {team.members.length > 0 && (
                  <div className="flex items-center ml-3">
                    {team.members.slice(0, 3).map((m, i) => (
                      <div
                        key={m.id}
                        className="-mr-[10px]"
                        style={{ zIndex: team.members.length - i }}
                      >
                        <UserAvatar
                          src={m.image ?? ""}
                          className="w-[28px] h-[28px] border-[2px] border-white"
                        />
                      </div>
                    ))}
                  </div>
                )}
              </div>

              <div className="flex gap-2">
                {(team.completion ?? 0) > 0 && (
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      navigate("/individual-results", {
                        state: { teamId: team.id, teamName: team.name },
                      });
                    }}
                    className="text-sm bg-green-50 text-green-700 px-3 py-1.5 rounded-lg hover:bg-green-100"
                  >
                    Team Results
                  </button>
                )}

                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    setAddingTeam(team.id);
                    setIsDropdownOpen(false);
                    setSearchTerm("");
                    setSelectedUser(null);
                    setActiveIndex(-1);
                  }}
                  className="text-sm bg-indigo-50 text-indigo-700 px-3 py-1.5 rounded-lg hover:bg-indigo-100"
                >
                  + Add Users
                </button>
              </div>
            </div>
          </div>

          {/* ADD USER (RIGHT SIDE) */}
          {addingTeam === team.id && (
            <div className="px-4 pb-4">
              <div className="flex flex-col sm:flex-row gap-3 sm:justify-end items-stretch">

                {/* DROPDOWN */}
                <div
                  ref={dropdownRef}
                  className="w-full sm:w-[220px] relative"
                >
                  <div onClick={(e) => e.stopPropagation()}>

                    {/* 🔍 SINGLE INPUT (SEARCH + SELECT) */}
                    <div className="flex items-center gap-2 border border-gray-200 rounded-lg px-2 py-1 bg-white shadow-sm">

                      {/* Avatar (if selected) */}
                      {selectedUser && (
                        <UserAvatar
                          src={
                            users.find((u) => u.value === selectedUser)?.image ?? ""
                          }
                          className="w-5 h-5"
                        />
                      )}

                      <input
                        type="text"
                        placeholder="Select User"
                        value={
                          searchTerm ||
                          (selectedUser
                            ? users.find((u) => u.value === selectedUser)?.label
                            : "")
                        }
                        onClick={() => setIsDropdownOpen(true)}
                        onChange={(e) => {
                          setSearchTerm(e.target.value);
                          setSelectedUser(null);
                          setIsDropdownOpen(true);
                          setActiveIndex(0);
                        }}
                        onKeyDown={(e) => {
                          if (!filteredUsers.length) return;

                          if (e.key === "ArrowDown") {
                            e.preventDefault();
                            setIsDropdownOpen(true);
                            setActiveIndex((prev) =>
                              prev < filteredUsers.length - 1 ? prev + 1 : 0
                            );
                          }

                          if (e.key === "ArrowUp") {
                            e.preventDefault();
                            setActiveIndex((prev) =>
                              prev > 0 ? prev - 1 : filteredUsers.length - 1
                            );
                          }

                          if (e.key === "Enter") {
                            e.preventDefault();
                            if (activeIndex >= 0) {
                              const selected = filteredUsers[activeIndex];
                              setSelectedUser(selected.value);
                              setSearchTerm("");
                              setActiveIndex(-1);
                              setIsDropdownOpen(false);
                            }
                          }

                          if (e.key === "Escape") {
                            setIsDropdownOpen(false);
                            setActiveIndex(-1);
                          }
                        }}
                        className="w-full text-sm py-1 focus:outline-none"
                      />

                      {/* Clear button */}
                      {selectedUser && (
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            setSelectedUser(null);
                            setSearchTerm("");
                          }}
                          className="text-gray-400 hover:text-gray-600 text-sm"
                        >
                          ✕
                        </button>
                      )}
                    </div>
                  </div>

                  {/* 🔽 DROPDOWN LIST */}
                  {isDropdownOpen && (
                    <div
                      ref={listRef}
                      className="mt-1 max-h-[220px] overflow-y-auto border border-gray-200 rounded-lg bg-white shadow-lg absolute z-20 w-full"
                    >
                      {filteredUsers.length > 0 ? (
                        filteredUsers.map((u, index) => (
                          <div
                            key={u.value}
                            onClick={(e) => {
                              e.stopPropagation();
                              setSelectedUser(u.value);
                              setSearchTerm("");
                              setActiveIndex(-1);
                              setIsDropdownOpen(false);
                            }}
                            className={`px-3 py-2 text-sm cursor-pointer flex items-center gap-2
                            ${index === activeIndex
                                ? "bg-[#e8edff]"
                                : "hover:bg-[#f4f6ff]"
                              }
                            `}
                          >
                            <UserAvatar src={u.image ?? ""} className="w-5 h-5" />
                            <span className="truncate">{u.label}</span>
                          </div>
                        ))
                      ) : (
                        <div className="px-3 py-2 text-sm text-gray-400">
                          No users found
                        </div>
                      )}

                    </div>
                  )}
                </div>
                {/* BUTTONS */}
                <div className="flex gap-2 w-full sm:w-auto">
                  <button
                    onClick={() => handleAttachUser(team)}
                    className="bg-[#3b519c] text-white px-3 py-2 rounded-md text-sm w-full sm:w-auto"
                  >
                    Attach
                  </button>

                  <button
                    onClick={() => {
                      setAddingTeam(null);
                      setSelectedUser(null);
                    }}
                    className="bg-gray-100 px-3 py-2 rounded-md text-sm w-full sm:w-auto"
                  >
                    Cancel
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* MEMBERS */}
          {/* MEMBERS */}
          {openTeam === team.id && (
            <div className="divide-y">
              {(() => {
                const allMembers = [
                  ...(team.lead
                    ? [{ ...team.lead, isLead: true }]
                    : []),
                  ...team.members
                    .filter((m) => m.id !== team.lead?.id) // جلوگیری از تکرار
                    .map((m) => ({ ...m, isLead: false })),
                ];

                return allMembers.map((member) => {
                  const pct = member.completed_per ?? 0;
                  const color = getProgressColor(pct);

                  const fullName =
                    member.name ??
                    `${member.first_name ?? ""} ${member.last_name ?? ""}`;

                  return (
                    <div
                      key={member.id}
                      className="px-5 py-4"
                    >
                      <div className="flex justify-between items-center">
                        <div className="flex gap-3">
                          <UserAvatar
                            src={member.image ?? ""}
                            className="w-[34px] h-[34px]"
                          />

                          <div>
                            <div className="font-medium text-gray-800 flex items-center gap-2">
                              {fullName}

                              {member.isLead && (
                                <span className="text-[10px] bg-indigo-100 text-indigo-700 px-2 py-0.5 rounded-full">
                                  Team Lead
                                </span>
                              )}
                            </div>

                            <div className="text-xs text-gray-400 truncate max-w-[180px] sm:max-w-none">
                              {member.email}
                            </div>
                          </div>
                        </div>

                        <div className="hidden sm:flex gap-2">
                          {pct === 100 && (
                            <button
                              onClick={() => {
                                const normalizedUser = {
                                  ...member,
                                  first_name:
                                    member.first_name ??
                                    member.name?.split(" ")[0] ??
                                    "",
                                  last_name:
                                    member.last_name ??
                                    member.name?.split(" ").slice(1).join(" ") ??
                                    "",
                                };

                                navigate("/individual-results", {
                                  state: { user: normalizedUser },
                                });
                              }}
                              className="text-[12px] px-3 py-2 rounded-lg bg-[#eef2ff] text-[#3b519c] hover:bg-[#3b519c] hover:text-white w-full sm:w-auto"
                            >
                              View
                            </button>
                          )}

                          {/* ❌ Disable remove for Team Lead */}
                          {!member.isLead && (
                            <button
                              onClick={() => setRemoveModal({ team, member })}
                              className="text-[12px] px-3 py-2 rounded-lg bg-red-50 text-red-600 hover:bg-red-600 hover:text-white w-full sm:w-auto"
                            >
                              Remove
                            </button>
                          )}
                        </div>
                      </div>

                      {/* PROGRESS BAR */}
                      <div className="flex items-center gap-3 mt-2">
                        <div className="w-[220px] h-[5px] bg-[#f0f3fa] rounded-full overflow-hidden">
                          <div
                            className="h-full rounded-full"
                            style={{
                              width: `${pct}%`,
                              background: color,
                            }}
                          />
                        </div>

                        <span
                          className="text-[12px] font-semibold"
                          style={{ color }}
                        >
                          {pct}%
                        </span>
                      </div>

                      {/* 📱 MOBILE BUTTONS */}
                      <div className="flex sm:hidden flex-col gap-2 pt-2 border-t mt-2">
                        {pct === 100 && (
                          <button
                            onClick={() => {
                              const normalizedUser = {
                                ...member,
                                first_name:
                                  member.first_name ??
                                  member.name?.split(" ")[0] ??
                                  "",
                                last_name:
                                  member.last_name ??
                                  member.name?.split(" ").slice(1).join(" ") ??
                                  "",
                              };

                              navigate("/individual-results", {
                                state: { user: normalizedUser },
                              });
                            }}
                            className="w-full px-3 py-2 rounded-lg bg-[#eef2ff] text-[#3b519c] text-sm font-medium"
                          >
                            View Results
                          </button>
                        )}

                        {!member.isLead && (
                          <button
                            onClick={() => setRemoveModal({ team, member })}
                            className="w-full px-3 py-2 rounded-lg bg-red-50 text-red-600 text-sm font-medium"
                          >
                            Remove Member
                          </button>
                        )}
                      </div>
                    </div>
                  );
                });
              })()}

              {/* REMOVE MODAL */}
              {removeModal.member && (
                <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50">
                  <div className="bg-white rounded-xl shadow-lg w-[90%] max-w-md p-5">
                    <h2 className="text-lg font-semibold text-gray-800">
                      Remove Member
                    </h2>

                    <p className="text-sm text-gray-600 mt-2">
                      Are you sure you want to remove{" "}
                      <span className="font-medium">
                        {removeModal.member.first_name ??
                          removeModal.member.name}{" "}
                        {removeModal.member.last_name ?? ""}
                      </span>{" "}
                      from the team?
                    </p>

                    <div className="flex justify-end gap-3 mt-5">
                      <button
                        onClick={() =>
                          setRemoveModal({ team: null, member: null })
                        }
                        className="px-4 py-2 rounded-md bg-gray-100 text-sm"
                      >
                        Cancel
                      </button>

                      <button
                        onClick={confirmRemoveUser}
                        className="px-4 py-2 rounded-md bg-red-600 text-white text-sm hover:bg-red-700"
                      >
                        Remove
                      </button>
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}

        </div>

      ))}
    </div>
  );
};

export default UserManagementAccordion;